import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useForm} from 'react-hook-form';
import {translations} from '../../../i18n/translations';
import {Alert, Grid, Skeleton, SkeletonProps, Stack} from '@mui/material';
import {Modal} from '../../modal/modal';
import {DynamicForm, DynamicFormFieldShape} from '../../dynamic-form';
import {LoadingConfigurationsType} from '../../../enums';
import {useAuth} from '../../../contexts/auth/context';
import {CarePlanConfiguration} from '../../../backend/interfaces/care-plan-configuration';
import {convertArrayToIndex} from '../../../utils/functions/convert-array-to-index';
import {EntityActivitiesRepository} from '../../../repositories/entity-activities';
import {DEFAULT_FORM_VALIDATIONS} from '../../../utils/constants';
import {ConfigurationsIndex} from '../../../types';
import {ActivityType} from '../../../backend/interfaces/activity-type';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../backend/requests/care-plan-activities-group';
import {EntityUserCarePlansRepository} from '../../../repositories/entity-user-care-plans';
import {useSnackBar} from '../../../contexts/snack-bar/context';
import {CarePlanActivitiesGroup} from '../../../backend/interfaces/care-plan-activities-group';
import {CarePlanActivitiesGroupType} from '../../../backend/interfaces/care-plan-activities-group-type';
import {CarePlanConfigurationLayout} from '../../../backend/interfaces/care-plan-configuration-layout';
import DeleteResourceConfirmationModal from '../../delete-resource-confirmation-modal';
import {EntityTemplatesRepository} from '../../../repositories/entity-templates';
import {useParams} from 'react-router-dom';

interface EditActivityEntryDialogProps {
  open: boolean;
  activityGroupEntry: CarePlanActivitiesGroup;
  entry: CarePlanActivitiesGroupType;
  seniorUserId?: string;
  activitiesSectorId: string;
  repetitionType?: 'activities-group' | 'activity-type';
  onClose: () => void;
  onUpdate: (entry: CarePlanActivitiesGroup) => void;
}

const SKELETON_DEFAULT_PROPS = {
  animation: 'wave',
  variant: 'rounded',
} as SkeletonProps;

export const EditActivityEntryDialog = ({
  open,
  onClose,
  entry,
  activityGroupEntry,
  seniorUserId,
  activitiesSectorId,
  onUpdate,
  repetitionType,
}: EditActivityEntryDialogProps) => {
  const authContext = useAuth();
  const snackBarContext = useSnackBar();
  const {t} = useTranslation();
  let {entityId, templateId}: {entityId: string; templateId: string} = useParams();
  const [activityType, setActivityType] = useState<ActivityType | undefined>(undefined);
  const [layout, setLayout] = useState<CarePlanConfigurationLayout | undefined>(undefined);
  const [loadingConfigurations, setLoadingConfigurations] = useState<LoadingConfigurationsType>(
    LoadingConfigurationsType.Loading
  );
  const [configurationsIndex, setConfigurationsIndex] = useState<ConfigurationsIndex>(undefined);
  const [defaultValuesIndex, setDefaultValuesIndex] = useState<any>(undefined);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const formInstance = useForm<DynamicFormFieldShape>({
    ...DEFAULT_FORM_VALIDATIONS,
  });

  const handleModalClose = () => {
    // reset form with empty values to clear all fields
    formInstance.reset({});

    // reset state variables to clear cached values
    setActivityType(undefined);
    setLayout(undefined);
    setConfigurationsIndex(undefined);
    setDefaultValuesIndex(undefined);
    setLoadingConfigurations(LoadingConfigurationsType.Loading);

    // close modal after cleanup
    onClose();
  };

  const loadActivityGroupEntryTypeConfigurations = useCallback(() => {
    // Immediately reset form to prevent showing previous activity's data
    formInstance.reset({});

    // Unset previous
    setActivityType(undefined);

    setDefaultValuesIndex(undefined);

    setConfigurationsIndex(undefined);

    setLayout(undefined);

    // Start Loading Configurations
    setLoadingConfigurations(LoadingConfigurationsType.Loading);

    // Obtain data
    Promise.all<[Promise<ActivityType | null>, Promise<Array<CarePlanConfiguration>>]>([
      // Load Activity Type (to obtain the Configuration layout + icon + name + etc)
      EntityActivitiesRepository.instance.getType(
        authContext.state.session?.selectedAffiliation?.entity.id ?? '',
        activitiesSectorId,
        activityGroupEntry.activitiesGroupId,
        entry.activityTypeId
      ),

      // Load Activity Type Configurations (to list the configuration values)
      EntityActivitiesRepository.instance.getTypeCarePlanConfigurations(
        authContext.state.session?.selectedAffiliation?.entity.id ?? '',
        activitiesSectorId,
        activityGroupEntry.activitiesGroupId,
        entry.activityTypeId
      ),
    ])
      .then(
        ([activityType, careplanConfigurations]: [
          ActivityType | null,
          Array<CarePlanConfiguration>,
        ]) => {
          if (activityType) {
            // set activity
            setActivityType(activityType);

            // index activity configurations
            setConfigurationsIndex(
              convertArrayToIndex<CarePlanConfiguration>(careplanConfigurations, 'id')
            );

            // set modal layout
            setLayout(activityType.carePlanConfigurationLayout);

            // set modal default values
            setDefaultValuesIndex(
              convertArrayToIndex<any>(entry.configurations ?? [], 'carePlanConfigurationId')
            );

            // Stop Loading
            setLoadingConfigurations(LoadingConfigurationsType.Loaded);
          } else {
            // Not found
            setLoadingConfigurations(LoadingConfigurationsType.Error);
          }
        }
      )
      .catch((error: any) => {
        // Log
        console.log(error);

        // Stop Loading
        setLoadingConfigurations(LoadingConfigurationsType.Error);
      });
  }, [
    activitiesSectorId,
    activityGroupEntry.activitiesGroupId,
    authContext.state.session?.selectedAffiliation?.entity.id,
    entry.activityTypeId,
    entry.configurations,
    formInstance,
  ]);

  const handleDeleteActivityTypeEntry = () => {
    if (seniorUserId) {
      // delete user care plan activity type
      EntityUserCarePlansRepository.instance
        .removeActivityTypeOnCarePlan(
          authContext.state.session?.selectedAffiliation?.entity.id ?? '',
          seniorUserId,
          activitiesSectorId,
          activityGroupEntry.id ?? '',
          entry.id ?? ''
        )
        .then(() => {
          // Show Toast
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.activities_type_entry.snackbar.delete.success),
          });

          // Remove from care plan
          let updatedEntry = {...activityGroupEntry};
          if (updatedEntry.activityTypes) {
            updatedEntry.activityTypes = updatedEntry.activityTypes.filter(
              (e) => e.id !== entry.id
            );

            // Fire update
            onUpdate(updatedEntry);
          }
        })
        .catch((_error) => {
          // Show Toast
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.activities_type_entry.snackbar.delete.error),
          });
        });
    } else {
      // delete template activity type
      EntityTemplatesRepository.instance
        .deleteTemplateActivityGroupType(
          entityId,
          templateId,
          activityGroupEntry.id ?? '',
          entry.id
        )
        .then(() => {
          // Show Toast
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.activities_type_entry.snackbar.delete.success),
          });

          // Remove from care plan
          let updatedEntry = {...activityGroupEntry};
          if (updatedEntry.activityTypes) {
            updatedEntry.activityTypes = updatedEntry.activityTypes.filter(
              (e) => e.id !== entry.id
            );
            // Fire update
            onUpdate(updatedEntry);
          }
        })
        .catch((_error) => {
          // Show Toast
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.activities_type_entry.snackbar.delete.error),
          });
        });
    }
    setConfirmDelete(false);

    handleModalClose();
  };

  const handleUpdateActivityTypeEntry = (
    formData: CarePlanActivitiesGroupApiRequestConfigurationsItem[]
  ) => {
    if (!entry.id) throw new Error('Empty sub activity entry id');

    if (seniorUserId) {
      // edit user care plan activity type
      EntityUserCarePlansRepository.instance
        .updateActivityTypeOnCarePlan(
          authContext.state.session?.selectedAffiliation?.entity.id ?? '',
          seniorUserId,
          activitiesSectorId,
          activityGroupEntry.id ?? '',
          entry.id ?? '',
          {
            activityTypeId: entry.activityTypeId,
            configurations: formData,
          }
        )
        .then((response) => {
          // Show Toast
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.activities_type_entry.snackbar.update.success),
          });

          // update callback
          let updatedEntry = {...activityGroupEntry};
          if (updatedEntry.activityTypes && response) {
            updatedEntry.activityTypes = updatedEntry.activityTypes.map((entry) => {
              if (entry.id === response.id) {
                return response;
              }
              return entry;
            });

            // Fire update
            onUpdate(updatedEntry);
          }
        })
        .catch((_error) => {
          // Show Toast
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.activities_type_entry.snackbar.update.error),
          });
        });
    } else {
      // edit template activity type
      EntityTemplatesRepository.instance
        .updateTemplateActivityGroupType(
          entityId,
          templateId,
          activityGroupEntry.id ?? '',
          entry.id,
          {
            activityTypeId: entry.activityTypeId,
            configurations: formData,
          }
        )
        .then((response) => {
          // Show Toast
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.activities_type_entry.snackbar.update.success),
          });

          // update callback
          let updatedEntry = {...activityGroupEntry};
          if (updatedEntry.activityTypes && response) {
            updatedEntry.activityTypes = updatedEntry.activityTypes.map((entry) => {
              if (entry.id === response.id) {
                return response;
              }
              return entry;
            });

            // Fire update
            onUpdate(updatedEntry);
          }
        })
        .catch((_error) => {
          // Show Toast
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.activities_type_entry.snackbar.update.error),
          });
        });
    }

    handleModalClose();
  };

  const taskName = useMemo(() => {
    if (!configurationsIndex || !defaultValuesIndex)
      return activityType?.name || t(translations.care_and_activities.task);

    let name = activityType?.name;

    for (let key in configurationsIndex) {
      const value = configurationsIndex[key];
      if (
        value?.feature === 'name' &&
        defaultValuesIndex[key]?.carePlanConfigurationValue &&
        String(defaultValuesIndex[key]?.carePlanConfigurationValue).length > 0
      ) {
        name = defaultValuesIndex[key]?.carePlanConfigurationValue;
      }
    }

    return name?.trim() || activityType?.name || t(translations.care_and_activities.task);
  }, [activityType?.name, configurationsIndex, defaultValuesIndex, t]);

  useEffect(() => {
    if (open) loadActivityGroupEntryTypeConfigurations();
  }, [loadActivityGroupEntryTypeConfigurations, open]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleModalClose}
        preventCloseClick
        title={t(translations.activities_type_entry.modals.edit.title)}
        aria-labelledby="edit-activity-group-entry-dialog-title"
        aria-describedby="edit-activity-group-entry-dialog-description"
        maxWidth="sm"
        fullWidth
        actions={[
          {
            label: t(translations.modal.cancel),
            type: 'destructive',
            onClick: handleModalClose,
          },
          {
            label: t(translations.modal.save),
            type: 'nondestructive',
            disabled:
              !formInstance.formState.isValid ||
              Object.entries(formInstance.formState.errors).length > 0,
            form: 'edit-activity-type-entry',
            buttonType: 'submit',
          },
        ]}
        actionButton={{type: 'destroy', callback: () => setConfirmDelete(true)}}
      >
        {loadingConfigurations === LoadingConfigurationsType.Loading && (
          <Grid container spacing={4}>
            {new Array(6).fill(1).map((_item: number, index: number) => (
              <Grid item xs={6} key={`skeleton_edit_sub_activity_${index}`}>
                <Stack gap={2} display="flex" flexDirection="column">
                  <Skeleton {...SKELETON_DEFAULT_PROPS} width={120} height={20} />
                  <Skeleton {...SKELETON_DEFAULT_PROPS} width="100%" height={42} />
                </Stack>
              </Grid>
            ))}
          </Grid>
        )}

        {loadingConfigurations === LoadingConfigurationsType.Error && (
          <Alert severity="error">{t(translations.dynamic_form.error_loading)}</Alert>
        )}

        {loadingConfigurations === LoadingConfigurationsType.Loaded && (
          <DynamicForm
            key={`edit-activity-type-entry-${entry.activityTypeId}-${open}`}
            id="edit-activity-type-entry"
            activityGroupId={activityGroupEntry.activitiesGroupId}
            activityTypeId={entry.activityTypeId}
            activitiesSectorId={activitiesSectorId}
            formInstance={formInstance}
            configurationsIndex={configurationsIndex}
            layout={layout}
            onSubmit={handleUpdateActivityTypeEntry}
            defaultValuesIndex={defaultValuesIndex}
            configuredRepetition={repetitionType}
          />
        )}
      </Modal>

      <DeleteResourceConfirmationModal
        id="confirm-delete-activity-type-entry-dialog"
        open={confirmDelete}
        title={t(translations.activities_type_entry.modals.delete.title, {type: taskName})}
        description={t(translations.activities_type_entry.modals.delete.copy.title, {
          type: `"${taskName}"`,
        })}
        cannotBeUndoneMessage={t(translations.activities_type_entry.modals.delete.copy.subtitle)}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDeleteActivityTypeEntry}
      />
    </>
  );
};
